'use client';

import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { noop } from '@interxfi/utilities/fn/no-op';

interface FrameLoaderContextShape {
  loadingStack: number;
  startLoading: VoidFunction;
  endLoading: VoidFunction;
}

export const FrameLoaderContext = createContext<FrameLoaderContextShape>({
  loadingStack: 0,
  startLoading: noop,
  endLoading: noop,
});

export function useFrameLoader() {
  return use(FrameLoaderContext);
}

export function useLoaderManager() {
  const [loadingStack, setLoadingStack] = useState(0);

  const startLoading = useCallback(() => {
    setLoadingStack((prevState) => prevState + 1);
  }, []);

  const endLoading = useCallback(() => {
    setLoadingStack((prevState) => Math.max(prevState - 1, 0));
  }, []);

  return useMemo(
    () => ({
      loadingStack,
      startLoading,
      endLoading,
    }),
    [loadingStack, startLoading, endLoading],
  );
}

export function FrameLoadingTrigger() {
  const { startLoading, endLoading } = useFrameLoader();

  useEffect(() => {
    startLoading();

    return () => {
      endLoading();
    };
  }, [startLoading, endLoading]);

  return null;
}
